
import { SEO } from '../../../utils/seo';
import GlobalNavbar from '../../../components/feature/GlobalNavbar';
import Footer from '../../../components/feature/Footer';
import ArticlePartOne from './components/ArticlePartOne';
import ArticlePartTwo from './components/ArticlePartTwo';
import ArticlePartThree from './components/ArticlePartThree';
import ArticlePartFour from './components/ArticlePartFour';

const DisabledVeteranPropertyTaxExemptionPage = () => {
  // Helper to safely create the ISO date string – falls back to an empty string
  // if `Date` is somehow unavailable (e.g., during SSR in a non‑browser env).
  const getIsoDate = () => {
    try {
      return new Date().toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="El Paso Disabled Veteran Property Tax Exemption: Complete VA Rating Guide | TREX RE LLC"
        description="Complete guide to the disabled veteran property tax exemption in El Paso Texas. Learn exemption amounts by VA rating, 100% disabled veteran total exemption, TDIU eligibility, Texas Form 50-135, calculator for savings, application steps, surviving spouse benefits, and EPCAD filing instructions."
        keywords="disabled veteran property tax exemption texas, 100 percent disabled veteran property tax exemption, disabled veteran property tax exemption application, disabled veteran property tax exemption texas calculator, texas disabled veteran property tax exemption form, 90 disabled veteran property tax exemption texas, what is a 12000 property tax exemption in texas, el paso texas, harris county disabled veteran property tax exemption"
        // Fixed typo in the URL (`veterarn` → `veteran`)
        canonical="/military/disabled-veteran-property-tax-exemption"
        schema={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline":
            "El Paso Disabled Veteran Property Tax Exemption: Complete VA Rating Guide",
          "description":
            "Comprehensive guide to the disabled veteran property tax exemption in El Paso Texas. Covers VA rating exemption amounts, 100% total exemption, TDIU eligibility, application forms, calculator, surviving spouse benefits, and step-by-step EPCAD filing instructions.",
          "author": {
            "@type": "Organization",
            "name": "TREX RE LLC",
          },
          "publisher": {
            "@type": "Organization",
            "name": "TREX RE LLC",
          },
          "datePublished": "2025-01-25",
          // Wrapped the date generation in a safe helper
          "dateModified": getIsoDate(),
          "mainEntityOfPage": {
            "@type": "WebPage",
            // Fixed typo here as well
            "@id":
              "https://trexrealestate.com/military/disabled-veteran-property-tax-exemption",
          },
        }}
      />

      <GlobalNavbar />

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/0636e188f9a15dcf8f0f8398c48dac5a.jpg"
            alt="Fort Bliss military base El Paso Texas where disabled veterans qualify for property tax exemptions"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/45 to-black/60"></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center w-full">
          <span className="inline-block px-4 py-2 bg-teal-600 text-white text-sm font-semibold rounded-full mb-6">
            Disabled Veteran Property Tax Guide
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            El Paso Disabled Veteran Property Tax Exemption: Complete VA Rating Guide
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto mb-6">
            Everything you need to know about the disabled veteran property tax exemption in
            El Paso Texas. VA rating amounts, 100% total exemption, TDIU eligibility, application
            forms, savings calculator, surviving spouse benefits, and step-by-step EPCAD filing
            instructions.
          </p>
          <div className="flex items-center justify-center gap-4 text-white/80 text-sm">
            <span>By TREX RE LLC</span>
            <span>&bull;</span>
            <span>January 2025</span>
            <span>&bull;</span>
            <span>22 min read</span>
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-10 bg-gray-50 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">In This Guide</h2>
          <div className="grid md:grid-cols-2 gap-3">
            <a
              href="#va-rating-scale"
              className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>VA Rating Scale &amp; Exemption Amounts</span>
            </a>
            <a
              href="#100-percent-exemption"
              className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>100% Total Exemption &amp; TDIU</span>
            </a>
            <a
              href="#calculator"
              className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>Savings Calculator &amp; Examples</span>
            </a>
            <a
              href="#forms"
              className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>Texas Form 50-135 &amp; Required Documents</span>
            </a>
            <a
              href="#application-process"
              className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>Step-by-Step Application Process</span>
            </a>
            <a
              href="#surviving-spouse"
              className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>Surviving Spouse Exemption</span>
            </a>
            <a
              href="#county-comparison"
              className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>El Paso vs. Harris County &amp; Others</span>
            </a>
            <a
              href="#mistakes"
              className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>Common Mistakes to Avoid</span>
            </a>
            <a
              href="#special-situations"
              className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>Rental, Inherited &amp; Special Situations</span>
            </a>
            <a
              href="#faq"
              className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>FAQ &amp; EPCAD Contact Info</span>
            </a>
          </div>
        </div>
      </section>

      <ArticlePartOne />
      <ArticlePartTwo />
      <ArticlePartThree />
      <ArticlePartFour />

      <Footer />
    </div>
  );
};

export default DisabledVeteranPropertyTaxExemptionPage;
